import { Inject, PLATFORM_ID, Component, Input, ViewEncapsulation, ViewChild, ElementRef, QueryList, ContentChildren, ContentChild, Renderer2, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { isPlatformServer } from '@angular/common';
import { ScrollingHelperService } from 'angular-engine/engine/service/ui/scrolling/scrolling.helper.service';
import { LazyViewport, LazyTarget } from 'angular-shared-features/feature/custom-image/lazy/lazy-viewport';
import { Observable } from 'rxjs';
import { Subject } from "rxjs/Subject";
import { merge } from 'rxjs/operators/merge';
import { map } from 'rxjs/operators/map';
import { switchMap } from 'rxjs/operators/switchMap';
import { combineLatest } from 'rxjs/operators/combineLatest';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { never } from 'rxjs/observable/never';
import { interval } from 'rxjs/observable/interval';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { startWith } from 'rxjs/operators/startWith';
import { takeUntil } from 'rxjs/operators/takeUntil'

@Component({
    selector: "vdgm-swiper",
    templateUrl: "./vdgm-swiper.template.html",
    styleUrls: ["./vdgm-swiper.style.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class VdgmSwiper {
    @Input() config: VdgmSwiperConfig;
    @ContentChildren('swiperSlide', { descendants: true, read: ElementRef }) slides: QueryList<ElementRef>;
    @ContentChild('swiperNext') swiperNext: ElementRef;
    @ContentChild('swiperPrev') swiperPrev: ElementRef;
    @ContentChild('scroller') scroller: ElementRef;

    clean$ = new Subject();
    focusedIndex: number = 0;
    offsetScrollX: number = 0;
    settings: VdgmSwiperConfig;

    protected get pageSize(): number {
        const numberOfVisibleItems: number = this.viewportItems.filter(item => item.intersectionRatio >= 0.95).length;

        if (numberOfVisibleItems)
            return numberOfVisibleItems;

        return 1;
    };

    protected get visibleElements(): number {
        const numberOfVisibleItems: number = this.viewportItems.filter(item => item.intersectionRatio > 0).length;

        if (numberOfVisibleItems)
            return numberOfVisibleItems;

        return 1;
    }

    protected nextArrowEnabled: boolean = true;
    protected prevArrowEnabled: boolean = true;
    protected startIndex = 1;
    protected endIndex = 1;
    protected ready = false;

    private viewportItems: Array<SwiperItem> = [];
    private lazyViewport: LazyViewport;
    private isServer: boolean;

    public visible: boolean = true;
    /**
     * VdgmSwiper
     */
    constructor(protected renderer: Renderer2,
        protected scrollHelper: ScrollingHelperService,
        protected cd: ChangeDetectorRef,
        protected el: ElementRef,
        @Inject(PLATFORM_ID) platformId: string) {
        this.isServer = isPlatformServer(platformId);
        this.scrollHelper.renderer = renderer;
    }

    ngOnInit() {
        this.lazyViewport = new LazyViewport();
        this.lazyViewport.setup(null, 0); //, 1.0

        this.settings = {
            loop: false,
            centered: false,
            pageSize: 1,
            gestures: false,
            spacing: 20,
            autoplay: 0
        } as VdgmSwiperConfig;

        if (this.config) {
            Object.assign(this.settings, this.config);
        }
    }

    ngAfterViewChecked() {
        this.setViewportItems();
    }

    ngAfterViewInit() {
        this.setViewportItems();
        if (!this.isServer) {
            if (this.settings.loop) {
                this.setupLoopingSlider();
            }
            else {
                this.renderer.addClass(this.scroller.nativeElement, "scrollable");
                this.ready = true;
            }

            if (this.settings.autoplay && this.settings.autoplay > 0) {
                this.setupAutoPlay(this.settings.autoplay);
            }

            this.setupResizingListener();
            this.toggleArrowVisibilityClass(this.focusedIndex);
        }
    }

    ngAfterContentInit() {
        if (this.slides.length > 0 && this.scroller) {
            if (this.swiperNext) {
                fromEvent(this.swiperNext.nativeElement, "click")
                    .pipe(takeUntil(this.clean$))
                    .subscribe((e) => { this.moveNext(e); });

                this.toggleArrowVisibilityClass(this.focusedIndex);
            }
            if (this.swiperPrev) {
                fromEvent(this.swiperPrev.nativeElement, "click")
                    .pipe(takeUntil(this.clean$))
                    .subscribe((e) => { this.moveBack(e); });
            }

            if (!this.isServer) {
                this.setActiveSlideClass(this.focusedIndex);
            }
        }
    }

    setViewportItems() {
        if (this.viewportItems.length) {
            this.viewportItems.forEach(slide => {
                this.lazyViewport.removeTarget(slide.target);
                slide.destroy();
            });

            this.viewportItems = [];
        }

        const swiperSlides = this.slides.toArray();

        swiperSlides.forEach(slide => {
            let viewportItem: SwiperItem = new SwiperItem(slide, () => { this.toggleArrowVisibilityClass(this.focusedIndex) });
            this.lazyViewport.addTarget(viewportItem.target);
            this.viewportItems.push(viewportItem);

            //configure slide appearance
            if (this.settings && this.settings.spacing) {
                this.renderer.setStyle(slide.nativeElement, "margin-right", this.settings.spacing + "px")
            }
        });
    }

    moveNext(e) {
        if (!this.nextArrowEnabled || !this.ready)
            return;

        let targetIndex = this.focusedIndex + this.pageSize;
        if (targetIndex >= this.slides.length - this.pageSize) {
            targetIndex = this.slides.length - this.pageSize;
        }
        let elem = this.slides.toArray()[targetIndex];
        this.scrollHorizontally(elem);
        this.setActiveSlideClass(targetIndex);
        this.toggleArrowVisibilityClass(targetIndex);
        this.focusedIndex = targetIndex;
    }

    moveBack(e) {
        if (!this.prevArrowEnabled || !this.ready)
            return;

        let targetIndex = this.focusedIndex - this.pageSize;

        if (targetIndex <= -1) {
            targetIndex = 0;
        }
        let elem = this.slides.toArray()[targetIndex];
        this.scrollHorizontally(elem);
        this.setActiveSlideClass(targetIndex);
        this.toggleArrowVisibilityClass(targetIndex);
        this.focusedIndex = targetIndex;
    }

    protected scrollHorizontally(elem) {
        let xPosition = elem.nativeElement.offsetLeft;
        if (this.settings.centered) {
            xPosition = elem.nativeElement.offsetLeft - (this.el.nativeElement.offsetWidth - elem.nativeElement.offsetWidth) / 2;
        }

        this.offsetScrollX = xPosition;
        this.scrollHelper.scrollElementToOffset({ elem: this.scroller, x: xPosition });
        if (this.settings.loop) {
            if (elem.nativeElement.classList.contains('clone-start')) {
                this.resetSlider(this.slides.find(e => e.nativeElement.classList.contains('first')));
            }
            else if (elem.nativeElement.classList.contains('clone-end')) {
                this.resetSlider(this.slides.find(e => e.nativeElement.classList.contains('last')));
            }
        }
        this.cd.detectChanges()
    }

    protected resetSlider(elem) {
        setTimeout(() => {
            this.renderer.removeClass(this.scroller.nativeElement, "scrollable");
            requestAnimationFrame(() => {
                let xPosition = elem.nativeElement.offsetLeft;

                if (this.settings.centered) {
                    xPosition = elem.nativeElement.offsetLeft - (this.el.nativeElement.offsetWidth - elem.nativeElement.offsetWidth) / 2;
                }

                this.offsetScrollX = xPosition;
                this.scrollHelper.scrollElementToOffset({ elem: this.scroller, x: xPosition });

                requestAnimationFrame(() => {
                    this.renderer.addClass(this.scroller.nativeElement, "scrollable");
                    if (elem.nativeElement.classList.contains('first')) {
                        this.focusedIndex = this.startIndex;
                    }
                    else {
                        this.focusedIndex = this.endIndex;
                    }
                });
            });
        }, 300);
    }

    public toggleArrowVisibilityClass(index) {
        if ((this.settings && this.settings.loop && this.containerHasOverflowSlides) || !this.swiperPrev || !this.swiperNext) return;
        let arrowVisibilityChanged = false;

        if (!this.containerHasOverflowSlides) {
            this.toggleNextArrow(false);
            this.togglePrevArrow(false);
            this.cd.detectChanges();
            return;
        }

        if (this.offsetScrollX <= this.slides.first.nativeElement.offsetLeft || index == 0) {
            if (this.prevArrowEnabled) {
                arrowVisibilityChanged = true;
            }
            this.togglePrevArrow(false);
        }
        else {
            if (!this.prevArrowEnabled) {
                arrowVisibilityChanged = true;
            }
            this.togglePrevArrow(true);
        }

        if (this.offsetScrollX + this.scroller.nativeElement.offsetWidth >= this.slides.last.nativeElement.offsetLeft + this.slides.last.nativeElement.offsetWidth) {
            if (this.nextArrowEnabled) {
                arrowVisibilityChanged = true;
            }
            this.toggleNextArrow(false);
        }
        else {
            if (!this.nextArrowEnabled) {
                arrowVisibilityChanged = true;
            }
            this.toggleNextArrow(true);
        }

        if (arrowVisibilityChanged)
            this.cd.detectChanges();
    }

    protected togglePrevArrow(state) {
        if (state) {
            if (!this.prevArrowEnabled) {
                this.renderer.removeClass(this.swiperPrev.nativeElement, "swiper-button-disabled");
                this.prevArrowEnabled = true;
            }
        }
        else {
            if (this.prevArrowEnabled) {
                this.renderer.addClass(this.swiperPrev.nativeElement, "swiper-button-disabled");
                this.prevArrowEnabled = false;
            }
        }
    }

    protected toggleNextArrow(state) {
        if (state) {
            if (!this.nextArrowEnabled) {
                this.renderer.removeClass(this.swiperNext.nativeElement, "swiper-button-disabled");
                this.nextArrowEnabled = true;
            }
        }
        else {
            if (this.nextArrowEnabled) {
                this.renderer.addClass(this.swiperNext.nativeElement, "swiper-button-disabled");
                this.nextArrowEnabled = false;
            }
        }
    }

    protected get containerHasOverflowSlides(): boolean {
        return this.scroller && this.scroller.nativeElement.scrollWidth > this.scroller.nativeElement.offsetWidth;
    }

    private setActiveSlideClass(targetIndex) {
        const allSlides = this.slides.toArray();
        requestAnimationFrame(() => {
            allSlides.forEach(elem => this.toggleActiveClass(elem, false));
        });
        requestAnimationFrame(() => {
            const targetSlide = allSlides[targetIndex];
            if (targetSlide) {
                this.toggleActiveClass(targetSlide);
                if (targetSlide.nativeElement.classList.contains('clone-start')) {
                    const first = this.slides.find(e => e.nativeElement.classList.contains('first'));
                    if (first) {
                        this.toggleActiveClass(first);
                    }
                }
                else if (targetSlide.nativeElement.classList.contains('clone-end')) {
                    const last = this.slides.find(e => e.nativeElement.classList.contains('last'));
                    if (last) {
                        this.toggleActiveClass(last);
                    }
                }
            }
        });
    }

    private toggleActiveClass(elem, state: boolean = true) {
        if (state) {
            this.renderer.addClass(elem.nativeElement, "active");
        } else {
            this.renderer.removeClass(elem.nativeElement, "active")
        }
    }

    private setupLoopingSlider() {
        setTimeout(() => {
            var elements: Array<ElementRef> = [...this.slides.toArray()];

            if(elements.length <= 1)
            {
                this.settings.loop = false;
                return;
            }

            var duplicationNumber = this.visibleElements;
            const slides = this.slides.toArray();

            for (var i = 0; i < duplicationNumber; i++) {
                const startel = slides[i].nativeElement.cloneNode(true);
                const endel = slides[slides.length - (i + 1)].nativeElement.cloneNode(true);

                if (i == 0) {
                    this.renderer.addClass(endel, "clone-end");
                    this.renderer.addClass(startel, "clone-start");
                }
                this.renderer.addClass(endel, "clone");
                this.renderer.addClass(startel, "clone");

                if (elements.length == 0) {
                    this.renderer.insertBefore(this.scroller.nativeElement, endel, this.slides.first.nativeElement);
                }
                else {
                    this.renderer.insertBefore(this.scroller.nativeElement, endel, elements[0].nativeElement);
                }

                this.renderer.appendChild(this.scroller.nativeElement, startel);

                elements.unshift(new ElementRef(endel));
                elements.push(new ElementRef(startel));
            }

            this.renderer.addClass(this.slides.first.nativeElement, "first");
            this.renderer.addClass(this.slides.last.nativeElement, "last");
            this.renderer.addClass(this.scroller.nativeElement, "looping");
            this.startIndex = duplicationNumber;
            this.endIndex = duplicationNumber + this.slides.length - 1;

            this.slides.reset(elements);

            const firstElement = this.slides.find(e => e.nativeElement.classList.contains('first'));
            let xPosition = firstElement.nativeElement.offsetLeft;
            if (this.settings.centered) {
                xPosition = firstElement.nativeElement.offsetLeft - (this.el.nativeElement.offsetWidth - firstElement.nativeElement.offsetWidth) / 2;
                this.renderer.addClass(this.scroller.nativeElement, "centered");
            }

            this.offsetScrollX = xPosition;
            this.scrollHelper.scrollElementToOffset({ elem: this.scroller, x: xPosition });
            this.focusedIndex = duplicationNumber;

            setTimeout(() => {
                this.renderer.addClass(this.scroller.nativeElement, "scrollable");
                this.ready = true;
            }, 100);

        }, 250);
    }

    private setupAutoPlay(delay) {
        // setup timer function to auto advance slides, 
        fromEvent(this.el.nativeElement, "mouseenter")
            .pipe(merge(fromEvent(this.el.nativeElement, "mouseleave")))
            .pipe(map(e => e["type"] === "mouseenter"))
            .pipe(startWith(false))
            .pipe(switchMap(paused => paused ? never() : interval(delay)))
            .pipe(takeUntil(this.clean$))
            .subscribe(tick => {
                this.moveNext({});
            });
    }

    private setupResizingListener() {
        fromEvent(window, "resize")
            .pipe(debounceTime(200))
            .pipe(takeUntil(this.clean$))
            .subscribe((e) => {
                this.scrollHorizontally(this.slides.toArray()[this.focusedIndex])
            });
    }

    ngOnDestroy() {
        this.clean$.next()
        if (this.viewportItems.length) {
            this.viewportItems.forEach(slide => {
                this.lazyViewport.removeTarget(slide.target);
                slide.destroy();
            });

            this.viewportItems = [];
        }

        this.lazyViewport.teardown();
    }
}

export interface VdgmSwiperConfig {
    loop: boolean;
    centered: boolean;
    pageSize: number;
    gestures: boolean;
    spacing: number;
    autoplay: number;
}

export class SwiperItem {
    private _target: LazyTarget;
    public elementReference: ElementRef;
    public intersectionRatio: number = 0;
    public visibilityCallback: Function;

    public get target(): LazyTarget {
        return this._target;
    }

    public destroy(): void {
        this.elementReference = null;
        this._target = null;
        this.intersectionRatio = 0;
        this.visibilityCallback = null;
    }

    constructor(elementReference: ElementRef, visibilityCallback: Function) {
        this.elementReference = elementReference;
        this.visibilityCallback = visibilityCallback;

        this._target = {
            element: elementReference.nativeElement,
            updateVisibility: (isVisible: boolean, ratio: number) => {
                this.intersectionRatio = ratio;
                this.visibilityCallback();
            }
        }
    }


}


// WEBPACK FOOTER //
// ./src/angular-framework-client-components/components/vdgm-swiper/vdgm-swiper.component.ts